/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target, Edit3, Save, X, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";

function fmtINR(n) {
  if (n == null) return "—";
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

const PACING_CONF = {
  on_track: { label: "On track", color: "text-emerald-300", bg: "bg-emerald-500/15", barFrom: "from-emerald-500", barTo: "to-emerald-400" },
  ahead:    { label: "Ahead",    color: "text-cyan-300",   bg: "bg-cyan-500/15",   barFrom: "from-cyan-500",   barTo: "to-fuchsia-400" },
  behind:   { label: "Behind",   color: "text-amber-300",  bg: "bg-amber-500/15",  barFrom: "from-amber-500",  barTo: "to-pink-500" },
};

export default function RevenueGoalWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/revenue/goal");
      setData(res);
      setDraft(String(res.target_inr));
    } catch (e) {
      toast.error("Failed to load revenue goal");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(draft, 10);
    if (!n || n < 1000) { toast.error("Enter at least 1,000"); return; }
    setSaving(true);
    try {
      await api.put("/admin/revenue/goal", { target_inr: n });
      toast.success("Monthly target updated");
      setEditing(false);
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !data) {
    return (
      <div className="glass rounded-2xl p-5 animate-pulse" data-testid="revenue-goal-loading">
        <div className="text-xs text-[var(--text-mute)]">Loading revenue goal…</div>
      </div>
    );
  }

  const pct = data.pct || 0;
  const pacing = PACING_CONF[data.pacing] || PACING_CONF.on_track;
  const TrendIcon = data.pacing === "behind" ? TrendingDown : TrendingUp;

  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="revenue-goal-widget">
      {/* gradient accent */}
      <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${pacing.barFrom} ${pacing.barTo}`} />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-mute)]">
            <Target className="w-3.5 h-3.5 text-pink-300" /> Monthly Revenue Goal
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-3xl font-bold text-white">{fmtINR(data.revenue_inr)}</div>
            <div className="text-sm text-[var(--text-mute)]">
              of {fmtINR(data.target_inr)} ({data.payments_count} payments)
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`px-2.5 py-1 rounded-full text-xs font-semibold ${pacing.bg} ${pacing.color} inline-flex items-center gap-1`}
               data-testid="revenue-pacing-badge">
            <TrendIcon className="w-3 h-3" /> {pacing.label}
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center gap-1"
                    data-testid="revenue-goal-edit">
              <Edit3 className="w-3 h-3" /> Set target
            </button>
          ) : (
            <div className="flex gap-1">
              <input type="number" value={draft} onChange={(e) => setDraft(e.target.value)}
                     className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs w-28 text-right"
                     data-testid="revenue-goal-input" />
              <button onClick={save} disabled={saving}
                      className="text-xs px-2.5 py-1.5 rounded-md bg-pink-500/20 text-pink-300 hover:bg-pink-500/30 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid="revenue-goal-save">
                <Save className="w-3 h-3" /> {saving ? "…" : "Save"}
              </button>
              <button onClick={() => { setEditing(false); setDraft(String(data.target_inr)); }}
                      className="text-xs p-1.5 rounded-md hover:bg-white/5" data-testid="revenue-goal-cancel">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar with expected-pacing marker */}
      <div className="mt-4 relative h-3 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${pacing.barFrom} ${pacing.barTo} transition-all duration-700`}
             style={{ width: `${Math.min(pct, 100)}%` }} />
        {/* Expected pacing line */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-white/40"
             style={{ left: `${Math.min(data.expected_pct, 100)}%` }}
             title={`Expected by today: ${data.expected_pct}%`} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-[var(--text-dim)] flex-wrap gap-2">
        <div data-testid="revenue-pct"><b className="text-white">{pct}%</b> complete · expected <b className="text-white">{data.expected_pct}%</b> by today</div>
        <div className="text-[var(--text-mute)]">
          Day {data.day_of_month} / {data.days_in_month} · <b className="text-pink-300">{data.days_left}</b> days left
        </div>
      </div>

      {data.pacing === "behind" && data.shortfall_inr > 0 && (
        <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-200" data-testid="revenue-behind-tip">
          <b>Behind target by {fmtINR(data.shortfall_inr)}.</b> Tip — fire the abandoned-cart job manually (Jobs · WA tab → Run Now) to recover stuck orders.
        </div>
      )}

      <RevenueHistoryChart currentLabel={data.month_start ? new Date(data.month_start).toLocaleString("en-US", { month: "short", year: "numeric" }) : null} />
    </div>
  );
}

function RevenueHistoryChart({ currentLabel }) {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/revenue/history?months=6")
      .then(({ data }) => setMonths(data.months || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!months.length) return null;

  // Highlight current month
  const targetINR = months[months.length - 1]?.target_inr || 0;
  const chartData = months.map((m) => ({
    label: m.label.split(" ")[0], // "Feb"
    revenue: m.revenue_inr,
    isCurrent: m.label === currentLabel,
  }));

  return (
    <div className="mt-5 pt-4 border-t border-white/5" data-testid="revenue-history-chart">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-mute)] mb-2">
        <BarChart3 className="w-3.5 h-3.5 text-cyan-300" /> 6-month revenue trend
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 0, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false}
                   tickFormatter={(v) => v >= 100000 ? `${Math.round(v/100000)}L` : v >= 1000 ? `${Math.round(v/1000)}k` : v} />
            <Tooltip
              contentStyle={{ background: "#0a0d18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#fff" }}
              formatter={(value) => [`₹${Math.round(value).toLocaleString("en-IN")}`, "Revenue"]}
            />
            {targetINR > 0 && (
              <ReferenceLine y={targetINR} stroke="#ec4899" strokeDasharray="4 4" label={{ value: "Target", fill: "#ec4899", fontSize: 10, position: "right" }} />
            )}
            <Bar dataKey="revenue" radius={[6, 6, 0, 0]}
                 fill="url(#revGrad)" />
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ec4899" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#a855f7" stopOpacity={0.7} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
